<template>
  <v-expansion-panels flat hide-actions v-model="localExpansionPanel">
    <v-expansion-panel eager>
      <template #title>
        <div>
          <v-icon
            icon="$chevron"
            color="primary"
            :class="[
              'text--semibold mr-2',
              {
                rotated: localExpansionPanel === 0,
              },
            ]"
          />
          <span class="subtitle1">
            {{ $t("network.secureWebGateway.allowBlockListsTitle") }}
          </span>
        </div>
      </template>
      <v-expansion-panel-text>
        <div class="d-flex align-center justify-space-between mb-4" @click.stop>
          <cui-filter-wrapper
            :show-clear-button="showClearFiltersButton"
            @clear-filters-clicked="clearFilters()"
          >
            <div class="filters-row d-flex align-center ga-2">
              <v-text-field
                v-model.trim="localFilters.search"
                data-testid="users-page-users-search"
                :class="{ 'filter-active': localFilters.search?.length }"
                density="compact"
                variant="outlined"
                rounded
                :placeholder="$t('general.search')"
                class="search-field"
                prepend-inner-icon="icon-search-xs"
                clearable
                clear-icon="icon-close-xs"
                hide-details
                :disabled="loading"
              />
              <cui-button-toggle-group
                :model-value="localFilters.allowed ?? ''"
                :items="listFilterItems"
                density="comfortable"
                bg-color="white"
                item-bg-color="indigo-sky"
                wrapper-rounded-class="rounded-xl"
                item-rounded-class="rounded-xl"
                @update:model-value="onListTypeChange"
              />
              <v-select
                v-model="localFilters.category"
                density="compact"
                class="filter-menu categories-filter"
                :items="categories"
                :class="{ 'filter-active': !!localFilters.category }"
                :placeholder="$t('network.secureWebGateway.table.category')"
                :disabled="loading"
                variant="outlined"
                rounded
                hide-details
              >
                <template #item="{ item, props }">
                  <v-list-item v-bind="props" title="">
                    <span v-text="$t(`network.secureWebGateway.filters.${item.value}`)" />
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <span v-text="$t(`network.secureWebGateway.filters.${item.raw}`)" />
                </template>
              </v-select>
              <v-select
                v-model="localFilters.label"
                density="compact"
                class="filter-menu labels-filter"
                :items="availableLabels"
                item-value="id"
                item-title="name"
                :class="{ 'filter-active': !!localFilters.label }"
                :placeholder="$t('network.secureWebGateway.labelsFilterPlaceholder')"
                :disabled="loading"
                variant="outlined"
                rounded
                clearable
                hide-details
                data-testid="swg-labels-filter"
              >
                <template #item="{ item, props }">
                  <v-list-item v-bind="props" title="">
                    <div class="d-flex align-center">
                      <div
                        class="color-preview mr-2"
                        :style="{ 'background-color': item.raw.color }"
                      />
                      <span v-text="item.raw.name" />
                    </div>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <div class="d-flex align-center">
                    <div
                      class="color-preview mr-2"
                      :style="{ 'background-color': item.raw.color }"
                    />
                    <span v-text="item.raw.name" />
                  </div>
                </template>
              </v-select>
            </div>
          </cui-filter-wrapper>
          <v-menu>
            <template #activator="{ props }">
              <v-btn
                :disabled="loading"
                v-bind="props"
                class="add-btn"
                color="primary"
                rounded
                large
              >
                <v-icon icon="$add" color="white" />
                <span v-text="$t('general.add')" />
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-for="actionItem in actionsList"
                :key="actionItem.action"
                :data-testid="`action-item-${actionItem.action}`"
                @click="onActionClick(actionItem)"
                :disabled="shouldDisableBlockAction(actionItem.action)"
              >
                <span v-text="$t(`network.secureWebGateway.actions.${actionItem.action}`)" />
                <template v-if="shouldDisableBlockAction(actionItem.action)" #append>
                  <span class="pointer-events-auto">
                    <v-tooltip location="top">
                      <template #activator="{ props }">
                        <v-icon
                          @click.native.prevent.stop.capture
                          v-bind="props"
                          color="grey-lighten-1"
                          icon="$info"
                        >
                        </v-icon>
                      </template>
                      <div
                        class="text-center w-100"
                        v-html="$t('network.secureWebGateway.disabledBlocklistTooltip')"
                      ></div>
                    </v-tooltip>
                  </span>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <secure-web-gateway-table
          :items="urlList"
          :total-items="total"
          :pagination="pagination"
          :loading="loading"
          :dns-allowlist-filtering-enabled="dnsAllowlistFilteringEnabled"
          :is-filtered="showClearFiltersButton"
          @item-action-applied="onActionClick"
          @page-change="onPageChange"
          @last-item-reached="onLastItemReached"
        />
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import SecureWebGatewayTable from "@/components/tables/SecureWebGateWayTable.vue";
import { CuiButtonToggleGroup, type CuiButtonToggleItem } from "@coronet/cui";
import {
  SecureWebGatewayAction,
  SecureWebGatewayCategories,
  SecureWebGatewayListFilterChoices,
} from "@/constants/network.ts";
import { FilterContext } from "@/_store/filters.module.ts";
import type { Pagination } from "@/types.ts";
import {
  type SecureWebGatewayUrlItem,
  useSWGStore,
} from "@/_store/network/swg/secure-web-gateway.module.ts";
import type { DeviceLabel } from "@/_store/devices-settings.module.ts";
import { CuiFilterWrapper } from "@coronet/cui";
import { useFilters } from "@/composables/useFilters.ts";

const filterContext = FilterContext.SECURE_WEB_GATEWAY;

export default defineComponent({
  components: {
    CuiFilterWrapper,
    SecureWebGatewayTable,
    CuiButtonToggleGroup,
  },
  props: {
    dnsAllowlistFilteringEnabled: {
      type: Boolean,
      required: true,
    },
    urlList: {
      type: Array as PropType<SecureWebGatewayUrlItem[]>,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    expansionPanel: {
      type: Number,
      default: 0,
    },
    availableLabels: {
      type: Array as PropType<DeviceLabel[]>,
      default: () => [],
    },
  },
  emits: ["action-click", "page-change", "last-item-reached"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const localExpansionPanel = ref(props.expansionPanel);
    const secureWebGatewayStore = useSWGStore();
    const { getSwgUrlList } = secureWebGatewayStore;

    const { clearFilters, showClearFiltersButton, localFilters } = useFilters({
      context: filterContext,
      searchKey: "search",
      onFilterChange: async () => {
        await getSwgUrlList();
      },
    });

    const categories = Object.values(SecureWebGatewayCategories);
    const listFilterItems: CuiButtonToggleItem<string>[] = [
      { value: "", label: t("general.all") },
      { value: SecureWebGatewayListFilterChoices.ALLOWED, label: t("general.allow") },
      { value: SecureWebGatewayListFilterChoices.BLOCKED, label: t("general.block") },
    ];

    const onListTypeChange = (val: string) => {
      // need type assertion because button group returns string
      localFilters.value.allowed = val as SecureWebGatewayListFilterChoices;
    };
    const actionsList = [
      {
        action: SecureWebGatewayAction.ADD_URL_TO_ALLOWLIST,
        item: { allowed: true },
      },
      {
        action: SecureWebGatewayAction.ADD_URL_TO_BLOCKLIST,
        item: { allowed: false },
      },
      {
        action: SecureWebGatewayAction.IMPORT_URL_GROUP_TO_ALLOWLIST,
        item: {
          allowed: true,
        },
      },
      {
        action: SecureWebGatewayAction.IMPORT_URL_GROUP_TO_BLOCKLIST,
        item: {
          allowed: false,
        },
      },
    ];

    const shouldDisableBlockAction = (action: SecureWebGatewayAction) => {
      const blockActions = [
        SecureWebGatewayAction.ADD_URL_TO_BLOCKLIST,
        SecureWebGatewayAction.IMPORT_URL_GROUP_TO_BLOCKLIST,
      ];
      return blockActions.includes(action) && props.dnsAllowlistFilteringEnabled;
    };

    const onActionClick = (payload: {
      action: SecureWebGatewayAction;
      item: { allowed: boolean };
    }) => {
      emit("action-click", payload);
    };

    const onPageChange = (payload: Pagination) => {
      emit("page-change", payload);
    };

    const onLastItemReached = () => {
      emit("last-item-reached");
    };

    return {
      localExpansionPanel,
      clearFilters,
      showClearFiltersButton,
      localFilters,
      categories,
      listFilterItems,
      onListTypeChange,
      actionsList,
      shouldDisableBlockAction,
      getSwgUrlList,
      onActionClick,
      onPageChange,
      onLastItemReached,
    };
  },
});
</script>

<style scoped lang="scss">
.filter-menu {
  width: 175px;
}

.color-preview {
  width: 16px;
  height: 16px;
  min-width: 16px;
  border-radius: 4px;
}

:deep(.button-toggle-wrapper) {
  border: 1px solid rgb(var(--v-theme-indigo-pale));
}

// TODO: remove workaround when PLAT-4103 is done
:deep(.v-field--focused) {
  .v-field__outline__start,
  .v-field__outline__end {
    border: 2px rgb(var(--v-theme-primary)) solid !important;
    opacity: 1 !important;
  }

  .v-field__outline__notch {
    &::before,
    &::after {
      border-color: rgb(var(--v-theme-primary)) !important;
      border-width: 0 0 2px !important;
      opacity: 1 !important;
    }
  }

  .v-field__outline__start {
    border-right: none !important;
  }

  .v-field__outline__end {
    border-left: none !important;
  }
}

:deep(*) {
  .v-expansion-panel-text__wrapper {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }

  .v-expansion-panel-title {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
}
</style>
